import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface InlineBPCaptureNodeProps {
  isDarkMode?: boolean;
  patientAgeYears?: number;
  onSave: (vitalPayload: any) => void;
  onAnswer: (answer: { conclusion: "optionA" | "optionB" }) => void;
  stepNumber?: number;
  existingSystolic?: number | null;
  existingDiastolic?: number | null;
}

export function InlineBPCaptureNode({
  isDarkMode = false,
  patientAgeYears,
  onSave,
  onAnswer,
  stepNumber,
  existingSystolic,
  existingDiastolic,
}: InlineBPCaptureNodeProps) {
  const [systolic, setSystolic] = useState("");
  const [diastolic, setDiastolic] = useState("");

  useEffect(() => {
    if (existingSystolic && existingSystolic > 0) {
      setSystolic(String(existingSystolic));
    } else {
      setSystolic("");
    }
    if (existingDiastolic && existingDiastolic > 0) {
      setDiastolic(String(existingDiastolic));
    } else {
      setDiastolic("");
    }
  }, [existingSystolic, existingDiastolic]);

  const systolicThreshold = (patientAgeYears ?? 0) >= 75 ? 150 : 140;
  const diastolicThreshold = 90;

  const sys = parseInt(systolic, 10);
  const dia = parseInt(diastolic, 10);
  const isValid = !isNaN(sys) && sys > 0 && !isNaN(dia) && dia > 0;

  const controlled = useMemo(
    () => isValid && sys < systolicThreshold && dia < diastolicThreshold,
    [isValid, sys, dia, systolicThreshold, diastolicThreshold],
  );

  const handleContinue = () => {
    if (!isValid) return;
    onSave({
      type: "blood-pressure",
      data: {
        systolic: systolic,
        diastolic: diastolic,
        unit: "mmHg",
      },
      timestamp: new Date(),
    });
    onAnswer({ conclusion: controlled ? "optionA" : "optionB" });
  };

  const card: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
    borderRadius: "1rem",
    padding: "1rem",
    backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: isDarkMode ? "#d1d5db" : "#374151",
    display: "block",
    marginBottom: "0.5rem",
  };

  const subLabelStyle: React.CSSProperties = {
    fontSize: "0.75rem",
    color: isDarkMode ? "#9ca3af" : "#6b7280",
  };

  const thresholdPanelStyle: React.CSSProperties = {
    marginBottom: "1rem",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.5rem",
    fontSize: "0.875rem",
    backgroundColor: isDarkMode ? "#374151" : "#f9fafb",
    color: isDarkMode ? "#d1d5db" : "#4b5563",
  };

  return (
    <div style={card}>
      {stepNumber !== undefined && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "1.5rem",
              height: "1.5rem",
              borderRadius: "9999px",
              fontSize: "0.875rem",
              fontWeight: 600,
              backgroundColor: isDarkMode ? "#ffffff" : "#000000",
              color: isDarkMode ? "#000000" : "#ffffff",
            }}
          >
            {stepNumber}
          </div>
          <h3
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              margin: 0,
              color: isDarkMode ? "#ffffff" : "#111827",
            }}
          >
            Record blood pressure
          </h3>
        </div>
      )}
      {stepNumber === undefined && (
        <h3
          style={{
            fontSize: "0.875rem",
            fontWeight: 600,
            marginBottom: "1rem",
            marginTop: 0,
            color: isDarkMode ? "#ffffff" : "#111827",
          }}
        >
          Record blood pressure
        </h3>
      )}

      <p
        style={{
          fontSize: "0.75rem",
          marginBottom: "1rem",
          color: isDarkMode ? "#9ca3af" : "#6b7280",
        }}
      >
        Enter the patient's blood pressure reading below. The result will be
        saved to the patient record and evaluated automatically.
      </p>

      <div style={{ marginBottom: "1rem" }}>
        <Label style={labelStyle}>Blood pressure (mmHg)</Label>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div style={{ flex: 1 }}>
            <p style={{ ...subLabelStyle, marginBottom: "0.25rem", marginTop: 0 }}>Systolic</p>
            <Input
              type="number"
              placeholder="120"
              value={systolic}
              onChange={(e) => setSystolic(e.target.value)}
            />
          </div>
          <span
            style={{
              fontSize: "1.25rem",
              fontWeight: 300,
              marginTop: "1rem",
              color: "#9ca3af",
            }}
          >
            /
          </span>
          <div style={{ flex: 1 }}>
            <p style={{ ...subLabelStyle, marginBottom: "0.25rem", marginTop: 0 }}>Diastolic</p>
            <Input
              type="number"
              placeholder="80"
              value={diastolic}
              onChange={(e) => setDiastolic(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div style={thresholdPanelStyle}>
        <p style={{ fontWeight: 500, marginBottom: "0.125rem", marginTop: 0 }}>
          Controlled threshold for this patient: &lt;{systolicThreshold}/
          {diastolicThreshold} mmHg
        </p>
        {(patientAgeYears ?? 0) >= 75 && (
          <p
            style={{
              fontSize: "0.75rem",
              marginTop: "0.125rem",
              marginBottom: 0,
              color: isDarkMode ? "#9ca3af" : "#6b7280",
            }}
          >
            Age 75 or over — adjusted threshold applies (150/90 mmHg)
          </p>
        )}
      </div>

      {isValid &&
        (controlled ? (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              backgroundColor: isDarkMode ? "rgba(6, 78, 59, 0.3)" : "#f0fdf4",
              border: `1px solid ${isDarkMode ? "#15803d" : "#bbf7d0"}`,
              color: isDarkMode ? "#86efac" : "#166534",
            }}
          >
            BP controlled — within threshold
          </div>
        ) : (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              backgroundColor: isDarkMode ? "rgba(120, 53, 15, 0.3)" : "#fffbeb",
              border: `1px solid ${isDarkMode ? "#b45309" : "#fde68a"}`,
              color: isDarkMode ? "#fcd34d" : "#92400e",
            }}
          >
            BP above threshold — Mysimba will be excluded
          </div>
        ))}

      <Button onClick={handleContinue} disabled={!isValid} size="lg" style={{ width: "100%" }}>
        Save and continue
      </Button>
    </div>
  );
}
